/**
 * `sponsored-invitation`: sponsored client setup WITHOUT a submitted intake
 * (crossmarc/steward-app#1543 part (b); founder-approved proof model, 2026-09-17).
 *
 * ## Why this is a separate type from `advisor-intake`
 *
 * When this type was added, the sponsored-setup path (`intakeClientWorkspace.ts`) proved the
 * recipient by three-way email match against a SUBMITTED intake (removed by
 * crossmarc/steward-app#1767, which sets up from the client record instead, see
 * `clientStewardSetup.ts`). This path has no intake to check against: the firm simply types an
 * address and Steward mails an invitation there, so the proof is "accepted from that
 * inbox" — the same mechanism appcore's founding-invite already uses for every other
 * invite in this system. Bolting that onto `advisor-intake` would mean synthesizing a fake
 * submission (rejected by design — see #1543's decision log) or weakening
 * the intake path's verification. Neither happened. This is its own row instead.
 *
 * ## Who writes it
 *
 * Only the façade's no-intake sponsored-invitation routes
 * (`services/steward-app-api/src/handlers/sponsoredSetup.ts`), under the firm caller's own
 * bearer token, into the FIRM's own workspace (`firm.firmWorkspaceId`, forced via
 * `runWithWorkspace`, exactly like the intake-based routes) — never the family's workspace,
 * and appcore is never touched directly (this repo's façade invariant).
 *
 * ## The two-phase write, what an absent `state` means, and why a retry RESUMES the row
 *
 * The row's id has to exist BEFORE the appcore call, because the row's own id is the
 * `sourceRef` (appcore's idempotency key) passed to `provisionSponsoredSetup`. So creation
 * is two phases: (1) get a row — either a fresh one, or (as of the 2026-09-17 fix below) an
 * earlier stateless attempt for the same address, RESUMED rather than duplicated — with only
 * `firmId`/`email`/`familyName`/`createdAt`/`createdBy` set, no `state`, no `workspaceId` —
 * then (2) call appcore, and on success, reconstruct-and-replace the row (read, spread, add
 * the link fields, write with the version just read — the same pattern `replaceLink` uses
 * for `clientWorkspace`) to set `workspaceId`/`tenantId`/`state: 'invited'`/`linkedAt`/
 * `linkedBy`.
 *
 * ⚠️ **Phase (1) alone does NOT make `sourceRef` an idempotency key** — reusing a stateless
 * row's id is what does. Until 2026-09-17 every call minted a brand-new row and so a
 * brand-new `sourceRef`, which meant appcore's `readExistingBySourceRef`
 * (`sponsored-setup-repository.ts` ~1236) could never find a prior attempt: a retry after
 * appcore committed but the phase-(2) write was lost left the ORIGINAL row permanently
 * stateless (a live founding invite pending, but un-cancellable — `requireLinkedWorkspace`
 * in `sponsoredSetup.ts` needs a `workspaceId` no stateless row has) and minted a SECOND
 * workspace besides. `createSponsoredInvitation`'s `scanInvitations` now looks for a
 * stateless row matching the requested address (by `throttleKey`, not `normalizeEmail` —
 * see that function's own docblock) BEFORE creating one, and reuses it when found. An
 * independent security review found the gap; `sponsoredSetupNoIntake.test.ts`'s resume test
 * pins the fix in both directions.
 *
 * A row with no `state` therefore means "an invitation was drafted but appcore never
 * confirmed a workspace for it" — NOT a live invitation. Every live/duplicate check in this
 * file and in `sponsoredSetup.ts` MUST treat an absent `state` as non-live, exactly as it
 * treats `cancelled`/`ended`. This mirrors `readIntakeClientWorkspaceLink`'s `absent` case,
 * with one difference worth being explicit about: an unreadable `clientWorkspace` value is
 * treated as LIVE (fail closed, since the row unambiguously represents a setup already in
 * flight); an unreadable ROW of THIS type is instead refused as a 409 by `readInvitation`
 * before any liveness question is asked, since the row is this type's only content.
 *
 * ## The states (identical vocabulary to `IntakeClientWorkspaceLink`, deliberately)
 *
 *   - (absent) — drafted, not yet linked to a workspace (see above). Never shown to the
 *                roster join; a retry after this state RESUMES this same row (see the
 *                two-phase-write note above) rather than minting a second one.
 *   - `invited`   — appcore set the workspace up and the family has not been seen to accept.
 *   - `active`    — the family accepted (learned the same way the intake path learns it:
 *                   `familyHasJoined`).
 *   - `cancelled` — the firm cancelled before the family accepted. Kept, never deleted.
 *   - `ended`     — the firm ended an accepted sponsorship.
 */
import { z } from 'zod';

export const SPONSORED_INVITATION_TYPE_CODE = 'sponsored-invitation';

export const SPONSORED_INVITATION_STATES = ['invited', 'active', 'cancelled', 'ended'] as const;
export type SponsoredInvitationState = (typeof SPONSORED_INVITATION_STATES)[number];

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();
/** Loose on purpose: this is a STORAGE-shape check, not the input validator. The façade's
 *  own zod input schema (`sponsoredSetup.ts`) is what rejects a malformed address before a
 *  row is ever written; this only has to reject what could not possibly be one. */
const emailShape = z.string().min(3).max(320);
const cleanedName = z.string().min(1).max(120);

export const SponsoredInvitationAttributesSchema = z.strictObject({
  firmId: id,
  /** The address the firm typed, as typed (not lowercased) — the value `provisionSponsoredSetup`
   *  and the invitation email both address. Duplicate/throttle comparisons normalize a COPY of
   *  this, never the stored value, so the record keeps what the firm actually entered. */
  email: emailShape,
  /** Sanitized via `cleanName` before storage (see `sponsoredSetup.ts`). Feeds
   *  `noIntakeWorkspaceName`; never sent to the invitation email itself (see that file's
   *  docblock on why the mailer takes no client-identifying field). */
  familyName: cleanedName,
  createdAt: instant,
  createdBy: id,
  workspaceId: id.optional(),
  tenantId: id.optional(),
  linkedAt: instant.optional(),
  linkedBy: id.optional(),
  state: z.enum(SPONSORED_INVITATION_STATES).optional(),
  lastInvitedAt: instant.optional(),
  cancelledAt: instant.optional(),
  cancelledBy: id.optional(),
  endedAt: instant.optional(),
  endedBy: id.optional(),
});

export type SponsoredInvitationAttributes = z.infer<typeof SponsoredInvitationAttributesSchema>;

/**
 * Reads a stored `sponsored-invitation` row's attributes. `unreadable` covers both "does not
 * parse" and "is missing" — unlike `readIntakeClientWorkspaceLink`, there is no `absent`
 * outer wrapper here because this schema describes the WHOLE row, not one optional key
 * nested inside a larger row; a row of this type that doesn't parse is just broken.
 */
export function readSponsoredInvitation(
  value: unknown,
): { kind: 'invitation'; invitation: SponsoredInvitationAttributes } | { kind: 'unreadable' } {
  const parsed = SponsoredInvitationAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'invitation', invitation: parsed.data } : { kind: 'unreadable' };
}

/** `invited` or `active` — the two states a live duplicate-check and the roster join must
 *  both treat as "this recipient already has something in flight". Absent, `cancelled` and
 *  `ended` are all NOT live: a firm may invite again in every one of those cases. */
export function sponsoredInvitationIsLive(state: SponsoredInvitationState | undefined): boolean {
  return state === 'invited' || state === 'active';
}
